import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { type AxiosInstance, isAxiosError } from "axios";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import type {
  DocumentDetail,
  DocumentPreviewPage,
  PreviewFilter,
} from "./types";

export const PREVIEW_PAGE_SIZE = 100;

const documentPath = (businessId: string, documentId: string) =>
  `/businesses/${businessId}/profitability_documents/${documentId}`;

const documentKey = (businessId: string, documentId: string) =>
  [...reportKey(businessId), "document", documentId] as const;

export const useDocumentDetail = (businessId: string, documentId: string) => {
  const api = useApi();
  return useQuery({
    queryKey: documentKey(businessId, documentId),
    queryFn: ({ signal }) =>
      api
        .get<{
          document: DocumentDetail;
        }>(documentPath(businessId, documentId), { signal })
        .then((res) => res.data.document),
  });
};

/** A search or a column's blanks narrow on the API, past the pages already loaded. */
export const useDocumentPreview = (
  businessId: string,
  documentId: string,
  filter: PreviewFilter,
) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...documentKey(businessId, documentId), "preview", filter],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      api
        .get<DocumentPreviewPage>(
          `${documentPath(businessId, documentId)}/preview`,
          {
            params: {
              page: pageParam,
              per: PREVIEW_PAGE_SIZE,
              ...(filter.sheet !== null ? { sheet: filter.sheet } : {}),
              ...(filter.query ? { q: filter.query } : {}),
              ...(filter.blank ? { blank: filter.blank } : {}),
            },
            signal,
          },
        )
        .then((res) => res.data),
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : null),
    // A new filter keeps the rows on screen until its own arrive.
    placeholderData: (previous) => previous,
  });
};

/**
 * A CSV or TSV is fetched as an .xlsx (`workbook`). Held a minute after the
 * view closes. A 422 is not retried: `workbookRefusal` reads why.
 */
export const useDocumentContent = (
  businessId: string,
  documentId: string,
  as: "content" | "workbook" = "content",
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...documentKey(businessId, documentId), as],
    queryFn: ({ signal }) =>
      api
        .get<ArrayBuffer>(`${documentPath(businessId, documentId)}/${as}`, {
          responseType: "arraybuffer",
          signal,
        })
        .then((res) => res.data),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: (failures, error) => workbookRefusal(error) === null && failures < 3,
  });
};

/**
 * Why the API would not serve a file as a workbook (its 422's `error`), or
 * null for any other failure. The body of a request for bytes arrives as
 * bytes, so it is decoded here.
 */
export const workbookRefusal = (error: unknown): string | null => {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;
  const body: unknown = error.response.data;
  try {
    const parsed: unknown =
      body instanceof ArrayBuffer
        ? JSON.parse(new TextDecoder().decode(body))
        : body;
    if (
      parsed &&
      typeof parsed === "object" &&
      "error" in parsed &&
      typeof parsed.error === "string"
    )
      return parsed.error;
  } catch {
    // Not JSON: still a refusal, without its reason.
  }
  return "This file is too large to open as a spreadsheet.";
};

/**
 * A short-lived link to the original file, as uploaded. The API answers with
 * the link rather than redirecting, because the request carries a header a
 * plain link cannot.
 */
export const fetchDownloadUrl = (
  api: AxiosInstance,
  businessId: string,
  documentId: string,
) =>
  api
    .get<{
      url: string;
      file_name: string;
    }>(`${documentPath(businessId, documentId)}/download`)
    .then((res) => res.data);
